import { useState, useRef, useEffect } from 'react';
import { processText, svgToDataUrl, parseArrowKey } from '../../../utils/learnUtils';
import { useMathJax } from '../../../hooks/useMathJax';
import { useCaptionHighlight } from '../../../hooks/useCaptionHighlight';
import SelectionToolbar from './SelectionToolbar';

/**
 * Manual multi-figure viewer — no scroll behaviour.
 *
 * Used in three contexts:
 *   1. Inline canvas frames whose data.multitype === 'manual'
 *   2. The figure lightbox (expand button on scroll-type multifigures)
 *   3. The SupportPanel when a multifigure ref-record is opened
 *
 * @param {{ images: string[], captions: string[], initialIdx?: number, onExpand?: () => void, captionMaxH?: number, backgroundImage?: string, showInstructions?: boolean, itemId?: string }} props
 * @prop itemId  When provided, enables per-panel caption highlighting.
 */
export default function MultiFigureViewer({ images, captions, initialIdx = 0, onExpand, captionMaxH, backgroundImage, showInstructions = false, outerNav = false, itemId }) {
  const count = images.length;
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(initialIdx, count - 1)));
  const captionRef     = useRef(null); // wrapper div — used for MathJax + pointer buttons
  const activeCaptionRef = useRef(null); // current figcaption — used for highlight tracking
  const [imgStyle, setImgStyle] = useState(null);
  const [activeKeys, setActiveKeys] = useState(new Set());

  // Keep activeCaptionRef in sync with the active panel
  useEffect(() => {
    const figcaptions = captionRef.current?.querySelectorAll('figcaption');
    activeCaptionRef.current = figcaptions?.[idx] ?? null;
  }, [idx]);

  const capHL = useCaptionHighlight(
    activeCaptionRef,
    itemId ? `${itemId}-cap-${idx}` : null,
  );

  useMathJax(captionRef, [idx], capHL.onTypesetRef);

  useEffect(() => { setActiveKeys(new Set()); }, [idx]);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    const onClick = (e) => {
      const btn = e.target.closest('.ct__fig-ptr-btn');
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.arrows;
      setActiveKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.querySelectorAll('.ct__fig-ptr-btn').forEach(btn => {
      btn.classList.toggle('ct__fig-ptr-btn--active', activeKeys.has(btn.dataset.arrows));
    });
  }, [activeKeys]);

  const visibleArrows = [...activeKeys].flatMap(parseArrowKey);

  // Only fires in lightbox context (captionMaxH provided); sizes all stacked images
  // to fill 85 % of the viewport while leaving room for caption and progress bar.
  const handleFirstLoad = (e) => {
    if (!captionMaxH) return;
    const { naturalWidth: nw, naturalHeight: nh } = e.target;
    if (!nw || !nh) return;
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const innerPad        = fs * 2.5;
    const captionOverhead = captionMaxH + fs;
    const progressOverhead = fs * 2;
    const maxW = window.innerWidth  * 0.85 - innerPad;
    const maxH = window.innerHeight * 0.85 - innerPad - captionOverhead - progressOverhead;
    const scale = Math.min(maxW / nw, maxH / nh);
    setImgStyle({ width: `${Math.round(nw * scale)}px`, height: `${Math.round(nh * scale)}px` });
  };

  const prev = () => setIdx(i => Math.max(0, i - 1));
  const next = () => setIdx(i => Math.min(count - 1, i + 1));

  return (
    <div className={`ct__mfig-viewer${outerNav ? ' ct__mfig-viewer--outer-nav' : ''}`}>

      {/* ── Image area with overlaid nav arrows ── */}
      <div className="ct__mfig-figure">
        <div className="ct__mfig-img-area">
          {backgroundImage && (
            <img
              src={svgToDataUrl(backgroundImage)}
              alt="Background"
              className="ct__mfig-img ct__mfig-img--background"
              style={imgStyle ?? undefined}
              onLoad={handleFirstLoad}
            />
          )}
          {images.map((svg, i) =>
            svg ? (
              <img
                key={i}
                src={svgToDataUrl(svg)}
                alt={`Panel ${i + 1}`}
                className={`ct__mfig-img ${i === idx ? 'ct__mfig-img--active' : ''}`}
                style={imgStyle ?? undefined}
                onLoad={!backgroundImage && i === 0 ? handleFirstLoad : undefined}
              />
            ) : null
          )}

          {visibleArrows.map(({ x, y }, i) => <PointerArrow key={i} x={x} y={y} />)}

          {onExpand && (
            <button
              className="ct__figure-expand"
              onClick={() => onExpand(idx)}
              aria-label="Expand figure"
              title="Expand figure"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 2h4M2 2v4M12 2H8M12 2v4M2 12h4M2 12V8M12 12H8M12 12V8"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}

          {count > 1 && (
            <button
              className="ct__mfig-nav ct__mfig-nav--prev"
              onClick={prev}
              disabled={idx === 0}
              aria-label="Previous figure"
            >
              <ChevronLeft />
            </button>
          )}
          {count > 1 && (
            <button
              className="ct__mfig-nav ct__mfig-nav--next"
              onClick={next}
              disabled={idx === count - 1}
              aria-label="Next figure"
            >
              <ChevronRight />
            </button>
          )}
        </div>

        {/* ── Captions — grid-stacked so height never shifts ── */}
        <div
          ref={captionRef}
          className="ct__mfig-captions"
          style={captionMaxH ? { maxHeight: `${captionMaxH}px`, overflowY: 'auto' } : undefined}
        >
          {captions.map((caption, i) => (
            <figcaption
              key={i}
              className={`ct__mfig-caption${i === idx ? ' ct__mfig-caption--active' : ''}`}
            >
              <span dangerouslySetInnerHTML={{ __html: processText(caption) }} />
              {showInstructions && count > 1 && i === 0 && (
                <span className="ct__mfig-instr">
                  <SlidersIcon />
                  <strong>Use the &#8220;forward&#8221; and &#8220;back&#8221; buttons to move between frames.</strong>
                </span>
              )}
            </figcaption>
          ))}
        </div>
      </div>

      {/* ── Progress segments (clickable to jump) ── */}
      <div className="ct__mfig-progress" role="tablist" aria-label="Figure panels">
        {images.map((_, i) => (
          <div
            key={i}
            role="tab"
            aria-selected={i === idx}
            className={`ct__mfig-seg${i <= idx ? ' ct__mfig-seg--done' : ''}`}
            onClick={() => setIdx(i)}
            style={{ cursor: count > 1 ? 'pointer' : 'default' }}
          />
        ))}
      </div>

      {capHL.toolbarPos && (
        <SelectionToolbar
          style={capHL.toolbarStyle}
          onCopy={capHL.copySelection}
          copyDone={capHL.copyDone}
          onPointerDown={capHL.preventDeselect}
          onHighlight={capHL.handleHighlight}
          onRemoveHighlight={capHL.handleRemoveHighlight}
        />
      )}

    </div>
  );
}

function SlidersIcon() {
  return (
    <svg className="ct__mfig-instr-icon" viewBox="0 0 16 14" fill="none" aria-hidden="true">
      <line x1="1" y1="2" x2="15" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="5" cy="2" r="1.5" fill="currentColor"/>
      <line x1="1" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="10" cy="7" r="1.5" fill="currentColor"/>
      <line x1="1" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
    </svg>
  );
}

function PointerArrow({ x, y }) {
  return (
    <svg
      className="ct__fig-ptr-arrow"
      style={{ left: `${x}%`, top: `${y}%` }}
      width="16" height="28" viewBox="0 0 16 28"
      aria-hidden="true"
    >
      <line x1="8" y1="0" x2="8" y2="17" stroke="white" strokeWidth="6" strokeLinecap="round"/>
      <polygon points="8,28 0,14 16,14" fill="white"/>
      <line x1="8" y1="1" x2="8" y2="17" stroke="var(--blue-primary)" strokeWidth="3" strokeLinecap="round"/>
      <polygon points="8,27 2,15 14,15" fill="var(--blue-primary)"/>
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M9 2L5 7l4 5" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M5 2l4 5-4 5" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
